import React, { useState, useRef, useEffect } from "react";
import { Button, message } from "antd";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  pdf,
} from "@react-pdf/renderer";
import { PDFDocument } from "pdf-lib";
import config from "../../../config";
import { ArrowLeftOutlined, DownloadOutlined } from "@ant-design/icons";
import { useMediaQuery } from "react-responsive";

const baseUrl = config.baseUrl;

// ─────────────────────────────────────────────
//  PALETA DE COLORES
// ─────────────────────────────────────────────
const COLOR_PRIMARY   = "#355F91"; // azul institucional
const COLOR_HEADER_BG = "#355F91";
const COLOR_HEADER_FG = "#FFFFFF";
const COLOR_GRAY_BG   = "#F5F5F5";
const COLOR_GRAY_MID  = "#BFBFBF";
const COLOR_BORDER    = "#CCCCCC";
const COLOR_TEXT      = "#1A1A1A";
const COLOR_MUTED     = "#555555";
const COLOR_FOOTER_BG = "#F0F4FA";

// ─────────────────────────────────────────────
//  ESTILOS
// ─────────────────────────────────────────────
const styles = StyleSheet.create({
  // ── Página ──────────────────────────────────
  page: {
    flexDirection: "column",
    paddingTop: 14,
    paddingRight: 36,
    paddingLeft: 36,
    paddingBottom: 30,
    fontFamily: "Helvetica",
    fontSize: 11,
    color: COLOR_TEXT,
    backgroundColor: "#FFFFFF",
  },

  // ────────────────────────────────────────────
  //  HEADER INSTITUCIONAL (3 columnas igual que el PDF de referencia)
  //  [LOGO ARN]  |  ACTA DE VISITA ASISTENCIA TÉCNICA  |  [CODIGO / FECHA / VERSION]
  // ────────────────────────────────────────────
  headerWrapper: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: COLOR_TEXT,
    marginBottom: 10,
  },
  headerLogo: {
    width: "22%",
    borderRightWidth: 1,
    borderRightColor: COLOR_TEXT,
    padding: 6,
    justifyContent: "center",
    alignItems: "center",
  },
  headerLogoImg: {
    width: "100%",
    height: 52,
    objectFit: "contain",
  },
  headerTitle: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 6,
    borderRightWidth: 1,
    borderRightColor: COLOR_TEXT,
  },
  headerTitleText: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
    color: COLOR_TEXT,
  },
  headerMeta: {
    width: "26%",
    flexDirection: "column",
  },
  headerMetaRow: {
    flex: 1,
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: COLOR_TEXT,
    paddingHorizontal: 6,
    paddingVertical: 3,
  alignItems: "center",
  justifyContent: "center", 
  },

  headerMetaRowLast: {
    flex: 1,
    flexDirection: "row",
    paddingHorizontal: 6,
    paddingVertical: 3,
    alignItems: "center",
    justifyContent:"center"
  },
  headerMetaLabel: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: COLOR_TEXT,
    marginRight: 4,
  },
  headerMetaValue: {
    fontSize: 8,
    fontFamily: "Helvetica",
    color: COLOR_TEXT,
  },
  headerMetaVersionRow: {
    flex: 1,
    flexDirection: "row",
  },
  headerMetaVersionCell: {
    flex: 1,
    paddingHorizontal: 6,
    paddingVertical: 3,
    alignItems: "center",
    justifyContent: "center",
  },
  headerMetaVersionDivider: {
    borderLeftWidth: 1,
    borderLeftColor: COLOR_TEXT,
  },

  // celda sub-header (fondo gris, texto negro negrita)
  tdSubHeader: {
    padding: 4,
    backgroundColor: COLOR_GRAY_MID,
    color: COLOR_TEXT,
    fontFamily: "Helvetica-Bold",
    fontSize: 10,
    textAlign: "center",
  },

  // ── Footer última página — empujado al fondo ─
footer: {
  position: 'absolute',   // ← clave
  bottom: 0,              // ← pegado al fondo
  left: 0,
  right: 0,
  borderTopWidth: 1,
  borderTopColor: COLOR_TEXT,
  backgroundColor: COLOR_HEADER_FG,
  padding: 5,
},
  footerText: {
    fontSize: 9,
    color: COLOR_MUTED,
    fontFamily: "Helvetica",
    lineHeight: 1,
    marginBottom: 4,
  },
  footerCode: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: COLOR_TEXT,
    textAlign: "right",
  },

  // ── Título de sección (fila azul oscuro) ─────
  sectionHeaderRow: {
    backgroundColor: COLOR_HEADER_BG,
    padding: 5,
    marginTop: 8,
  },
  sectionHeaderText: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    color: COLOR_HEADER_FG,
    textAlign: "center",
  },
  // título gris claro (HALLAZGOS, RECOMENDACIONES…)
  sectionGrayRow: {
    backgroundColor: COLOR_GRAY_MID,
    padding: 5,
    marginTop: 8,
    borderWidth: 1,
    borderColor: COLOR_TEXT,
  },
  sectionGrayText: {
    fontSize:11,
    fontFamily: "Helvetica-Bold",
    color: COLOR_TEXT,
    textAlign: "center",
  },

  // ── Tablas genéricas ─────────────────────────
  table: {
    width: "100%",
    borderWidth: 1,
    borderColor: COLOR_TEXT,
    borderStyle: "solid",
    marginTop: 12,   // separación respecto al bloque anterior
    marginBottom: 0,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: COLOR_TEXT,
  },
  tableRowLast: {
    flexDirection: "row",
  },
  // celda izquierda (label) — 40%
  tdLabel: {
    width: "40%",
    padding: 4,
    borderRightWidth: 1,
    borderRightColor: COLOR_TEXT,
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: COLOR_TEXT,
    justifyContent: "center",
  },
  // celda derecha (valor) — 60%
  tdValue: {
    width: "60%",
    padding: 4,
    fontSize: 11,
    fontFamily: "Helvetica",
    color: COLOR_TEXT,
    justifyContent: "center",
  },
  // celdas para tablas de 3 columnas iguales
  td33: {
    flex: 1,
    padding: 4,
    borderRightWidth: 1,
    borderRightColor: COLOR_TEXT,
    fontSize: 11,
    color: COLOR_TEXT,
  },
  td33Last: {
    flex: 1,
    padding: 4,
    fontSize: 10,
    color: COLOR_TEXT,
  },
  tdHeader: {
    padding: 4,
    backgroundColor: COLOR_HEADER_BG,
    color: COLOR_HEADER_FG,
    fontFamily: "Helvetica-Bold",
    fontSize: 10,
    textAlign: "center",
  },

  // ── Tabla "QUIEN RECIBE" con checkboxes ──────
  checkRow: {
    flexDirection: "row",
    alignItems: "center",
    marginRight: 16,
  },
  checkBox: {
    width: 10,
    height: 10,
    borderWidth: 1,
    borderColor: COLOR_TEXT,
    marginRight: 4,
    justifyContent: "center",
    alignItems: "center",
  },
  checkMark: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    color: COLOR_TEXT,
    lineHeight: 1,
  },
  checkLabel: {
    fontSize: 11,
    color: COLOR_TEXT,
  },

  // ── Texto libre (hallazgos, recomendaciones) ─
  freeTextBox: {
    borderWidth: 1,
    borderTopWidth: 0,
    borderColor: COLOR_TEXT,
    padding: 6,
    minHeight: 40,
  },
  freeText: {
    fontSize: 11,
    lineHeight: 1.5,
    textAlign: "justify",
    color: COLOR_TEXT,
    fontFamily: "Helvetica",
  },

  // ── Firmas (tabla estilo referencia) ─────────
  signatureTable: {
    width: "100%",
    borderWidth: 1,
    borderColor: COLOR_TEXT,
    marginTop: 8,
  },
  signatureTableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: COLOR_TEXT,
  },
  signatureTableRowLast: {
    flexDirection: "row",
  },
  signatureLabelCell: {
    width: "38%",
    padding: 4,
    borderRightWidth: 1,
    borderRightColor: COLOR_TEXT,
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: COLOR_TEXT,
    justifyContent: "center",
  },
  signatureValueCell: {
    flex: 1,
    padding: 4,
    fontSize: 11,
    color: COLOR_TEXT,
    justifyContent: "center",
  },
  signatureImgCell: {
    flex: 1,
    minHeight: 70,
    padding: 4,
    justifyContent: "center",
    alignItems: "flex-start",
  },
  signatureImg: {
    width: 130,
    height: 65,
    objectFit: "contain",
  },

  // ── ANEXOS ───────────────────────────────────
  annexBlock: {
    borderWidth: 1,
    borderColor: COLOR_TEXT,
    marginTop: 8,
  },
  annexHeader: {
    backgroundColor: COLOR_GRAY_MID,
    padding: 4,
    borderBottomWidth: 1,
    borderBottomColor: COLOR_TEXT,
  },
  annexHeaderText: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
    color: COLOR_TEXT,
  },
  annexSubHeader: {
    padding: 4,
    borderBottomWidth: 1,
    borderBottomColor: COLOR_TEXT,
    backgroundColor: "#FFFFFF",
  },
  annexSubHeaderText: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    color: COLOR_TEXT,
  },
  annexPhotoArea: {
    padding: 6,
    borderBottomWidth: 1,
    borderBottomColor: COLOR_TEXT,
  },
  annexPhotoAreaLast: {
    padding: 6,
  },
  imageRow: {
    flexDirection: "row",
    marginBottom: 6,
  },
  image: {
    width: "48%",
    height: 180,
    marginRight: "2%",
    objectFit: "contain",
    marginTop: 5
  },
  imageLast: {
    marginRight: 0,
  },
  noPhoto: {
    fontSize: 8.5,
    color: COLOR_MUTED,
    fontFamily: "Helvetica",
    padding: 4,
  },
});

// ─────────────────────────────────────────────
//  UTILIDAD
// ─────────────────────────────────────────────
const chunkArray = (array, chunkSize) => {
  const chunks = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
};

// ─────────────────────────────────────────────
//  COMPONENTE: bloque de fotos dentro de ANEXOS
//  wrap={false} en cada fila para evitar cortes
// ─────────────────────────────────────────────
const PhotoBlock = ({ label, photos, baseUrl, isLast = false }) => (
  <View style={isLast ? styles.annexPhotoAreaLast : styles.annexPhotoArea}>
    <View style={styles.annexSubHeader}>
      <Text style={styles.annexSubHeaderText}>{label}</Text>
    </View>
    {photos && photos.length > 0 ? (
      chunkArray(photos, 2).map((chunk, rowIndex) => (
        <View key={rowIndex} style={styles.imageRow} wrap={false}>
          {chunk.map((foto, index) => (
            <Image
              key={index}
              style={[styles.image, index === chunk.length - 1 && styles.imageLast]}
              source={{ uri: `${baseUrl}${foto}` }}
            />
          ))}
        </View>
      ))
    ) : (
      <Text style={styles.noPhoto}>No hay fotografías registradas.</Text>
    )}
  </View>
);

// ─────────────────────────────────────────────
//  COMPONENTE: checkbox visual
// ─────────────────────────────────────────────
const CheckBox = ({ checked, label }) => (
  <View style={styles.checkRow}>
    <View style={styles.checkBox}>
      {checked && <Text style={styles.checkMark}>X</Text>}
    </View>
    <Text style={styles.checkLabel}>{label}</Text>
  </View>
);

// ─────────────────────────────────────────────
//  DOCUMENTO PDF
// ─────────────────────────────────────────────
const MyPDF = ({ formData }) => {
  if (!formData) {
    return (
      <Document>
        <Page style={styles.page}>
          <Text>No hay datos disponibles</Text>
        </Page>
      </Document>
    );
  }

  const esTitular =
    !formData?.quien_resibe_visita ||
    formData?.quien_resibe_visita?.toLowerCase().includes("titular");

  return (
    <Document>
      <Page style={styles.page}>

        {/* ══════════════════════════════════════
            HEADER INSTITUCIONAL — se repite en cada página
            [LOGO]  |  ACTA DE VISITA  |  CODIGO/FECHA/VERSION
        ══════════════════════════════════════ */}
        <View style={styles.headerWrapper} fixed>
          {/* Logo ARN */}
          <View style={styles.headerLogo}>
            <Image style={styles.headerLogoImg} src={"Logo_ARN.png"} />
          </View>

          {/* Título central */}
          <View style={styles.headerTitle}>
            <Text style={styles.headerTitleText}>ACTA DE VISITA ASISTENCIA TÉCNICA</Text>
          </View>

          {/* Código / Fecha / Versión */}
          <View style={styles.headerMeta}>
            {/* Fila CÓDIGO */}
            <View style={styles.headerMetaRow}>
              <Text style={styles.headerMetaLabel}>CODIGO :</Text>
              <Text style={styles.headerMetaValue}>IR-F-214</Text>
            </View>
            {/* Fila FECHA + VERSION */}
            <View style={styles.headerMetaVersionRow}>
              <View style={styles.headerMetaVersionCell}>
                <Text style={styles.headerMetaLabel}>FECHA :</Text>
                <Text style={styles.headerMetaValue}>2025-09-12</Text>
              </View>
              <View style={[styles.headerMetaVersionCell, styles.headerMetaVersionDivider]}>
                <Text style={styles.headerMetaLabel}>VERSION :</Text>
                <Text style={styles.headerMetaValue}>V-1</Text>
              </View>
            </View>
          </View>
        </View>

        {/* ══════════════════════════════════════
            TEMÁTICA DE LA VISITA
        ══════════════════════════════════════ */}
        <View style={styles.table}>
          {/* Título dentro de la tabla */}
          <View style={[styles.tableRow, { backgroundColor: COLOR_HEADER_BG }]}>
            <Text style={[styles.tdHeader, { flex: 1, borderRightWidth: 0 }]}>TEMÁTICA DE LA VISITA</Text>
          </View>
          {/* Sub-headers de columnas — fondo gris */}
          <View style={styles.tableRow}>
            <Text style={[styles.tdSubHeader, { flex: 1, borderRightWidth: 1, borderRightColor: COLOR_TEXT }]}>FACTOR DE SOSTENIBILIDAD</Text>
            <Text style={[styles.tdSubHeader, { flex: 1, borderRightWidth: 1, borderRightColor: COLOR_TEXT }]}>ACTIVIDAD DERIVADA</Text>
            <Text style={[styles.tdSubHeader, { flex: 1 }]}>SUBACTIVIDAD</Text>
          </View>
          {/* Filas de datos */}
          {Array.isArray(formData.lista_factores) && formData.lista_factores.length > 0 ? (
            formData.lista_factores.map((item, i, arr) => (
              <View key={i} style={i < arr.length - 1 ? styles.tableRow : styles.tableRowLast}>
                <Text style={[styles.td33, { fontFamily: "Helvetica-Bold" }]}>{item.factor || ""}</Text>
                <Text style={styles.td33}>{item.actividad || ""}</Text>
                <Text style={styles.td33Last}>{item.subActividad || ""}</Text>
              </View>
            ))
          ) : (
            <View style={styles.tableRowLast}>
              <Text style={[styles.td33Last, { flex: 1 }]}></Text>
            </View>
          )}
        </View>

        {/* ══════════════════════════════════════
            INFORMACIÓN GENERAL
        ══════════════════════════════════════ */}


        <View style={styles.table} wrap>
          <View style={[styles.tableRow, { backgroundColor: COLOR_HEADER_BG }]}>
    <Text style={[styles.tdHeader, { flex: 1, borderRightWidth: 0 }]}>
      INFORMACIÓN GENERAL
    </Text>
  </View>
          {/* Visita N° — fila completa */}
          <View style={styles.tableRow}>
            <Text style={[styles.tdLabel, { width: "30%" }]}>VISITA IMPLEMENTACIÓN N°</Text>
            <Text style={[styles.tdValue, { width: "70%" }]}>{formData?.numero_visita || ""}</Text>
          </View>

          {/* Fecha + Grupo territorial en la misma fila */}
          <View style={styles.tableRow}>
            <Text style={[styles.tdLabel, { width: "30%" }]}>FECHA VISITA</Text>
            <Text style={[styles.tdValue, { width: "20%", borderRightWidth: 1, borderRightColor: COLOR_TEXT }]}>{formData?.fecha_registro || ""}</Text>
            <Text style={[styles.tdLabel, { width: "30%" }]}>GRUPO TERRITORIAL</Text>
            <Text style={[styles.tdValue, { flex: 1 }]}>{formData?.grupoTerritorial || ""}</Text>
          </View>

          {/* Departamento + Municipio */}
          <View style={styles.tableRow}>
            <Text style={[styles.tdLabel, { width: "30%" }]}>DEPARTAMENTO</Text>
            <Text style={[styles.tdValue, { width: "20%", borderRightWidth: 1, borderRightColor: COLOR_TEXT }]}>{formData?.departamento || ""}</Text>
            <Text style={[styles.tdLabel, { width: "30%" }]}>MUNICIPIO</Text>
            <Text style={[styles.tdValue, { flex: 1 }]}>{formData?.municipio || ""}</Text>
          </View>

          {/* Dirección + Código PRE */}
          <View style={styles.tableRow}>
            <Text style={[styles.tdLabel, { width: "30%" }]}>DIRECCIÓN EXACTA DEL PROYECTO{"\n"}(VEREDA/CORREGIMIENTO SI APLICA)</Text>
            <Text style={[styles.tdValue, { width: "20%", borderRightWidth: 1, borderRightColor: COLOR_TEXT }]}>{formData?.vereda || ""}</Text>
            <Text style={[styles.tdLabel, { width: "30%" }]}>CODIGO PRE</Text>
            <Text style={[styles.tdValue, { flex: 1 }]}>{formData?.codigoPREPP || ""}</Text>
          </View>

          {/* Nombre proyecto */}
          <View style={styles.tableRow}>
            <Text style={[styles.tdLabel, { width: "30%" }]}>NOMBRE DEL PROYECTO PRODUCTIVO (ACTUAL)</Text>
            <Text style={[styles.tdValue, { flex: 1 }]}>{formData?.nombreProyecto || ""}</Text>
          </View>

          {/* Quien recibe — con checkboxes */}
          <View style={styles.tableRow}>
            <Text style={[styles.tdLabel, { width: "30%" }]}>
              QUIEN RECIBE LA VISITA:{"\n"}MARQUE CON UNA "X" LA OPCIÓN SEGÚN CORRESPONDA
            </Text>
             <Text style={[styles.tdLabel, { width: "20%" }]}> TITULAR DEL PROYECTO</Text>
             <Text style={[styles.tdLabel, { width: "30%",  textAlign: 'center' }]}>{esTitular && "X"}</Text>
            <Text style={[styles.tdLabel, { width: "13%" }]}>DELEGADO</Text>
             <Text style={[styles.tdValue, { flex: 1, textAlign: 'center'}]}> {!esTitular && "X"}</Text>
          </View>

          {/* Nombre titular */}
          <View style={styles.tableRow}>
            <Text style={[styles.tdLabel, { width: "30%" }]}>NOMBRE COMPLETO DEL TITULAR DEL PROYECTO/DELEGADO</Text>
            <Text style={[styles.tdValue, { flex: 1 }]}>{formData?.nombre_titular_proyecto || ""}</Text>
          </View>

          {/* Identificación + SIRR */}
          <View style={styles.tableRow}>
            <Text style={[styles.tdLabel, { width: "30%" }]}>NÚMERO DE IDENTIFICACIÓN DEL TITULAR O DELEGADO</Text>
            <Text style={[styles.tdValue, { width: "20%", borderRightWidth: 1, borderRightColor: COLOR_TEXT }]}>{formData?.identificacion_titular_proyecto || ""}</Text>
            <Text style={[styles.tdLabel, { width: "30%" }]}>CÓDIGO INDIVIDUO SIRR (SI APLICA)</Text>
            <Text style={[styles.tdValue, { flex: 1 }]}>{formData?.codigo_titular_proyecto || ""}</Text>
          </View>

          {/* Hora inicio + Hora fin */}
          <View style={styles.tableRow}>
            <Text style={[styles.tdLabel, { width: "30%" }]}>HORA INICIO</Text>
            <Text style={[styles.tdValue, { width: "20%", borderRightWidth: 1, borderRightColor: COLOR_TEXT }]}>{formData?.horaInicio || ""}</Text>
            <Text style={[styles.tdLabel, { width: "30%" }]}>HORA FINALIZACIÓN</Text>
            <Text style={[styles.tdValue, { flex: 1 }]}>{formData?.horaFin || ""}</Text>
          </View>

          {/* N° personas */}
          <View style={styles.tableRow}>
            <Text style={[styles.tdLabel, { width: "30%" }]}>
              NUMERO DE PERSONAS EN PROCESO DE REINCORPORACION DEL PROCESO QUE CONFORMAN EL PROYECTO:
            </Text>
            <Text style={[styles.tdValue, { flex: 1 }]}>{formData?.numeroPersonasParticipan || ""}</Text>
          </View>

          {/* Sector productivo */}
          <View style={styles.tableRowLast}>
            <Text style={[styles.tdLabel, { width: "30%" }]}>SECTOR PRODUCTIVO DEL PROYECTO:</Text>
            <Text style={[styles.tdValue, { flex: 1 }]}>{formData?.linea_productiva || ""}</Text>
          </View>
        </View>

        {/* ══════════════════════════════════════
            ACTIVIDADES DESARROLLADAS
        ══════════════════════════════════════ */}
        <View style={[styles.sectionHeaderRow, { marginTop: 12 }]}>
          <Text style={styles.sectionHeaderText}>ACTIVIDADES DESARROLLADAS:</Text>
        </View>
        {/* subtítulo descripción */}
        <View style={[styles.table, { marginTop: 0, borderTopWidth: 0 }]}>
          <View style={[styles.tableRow, { backgroundColor: COLOR_GRAY_MID }]}>
            <Text style={[styles.tdLabel, { width: "100%", borderRightWidth: 0, fontFamily: "Helvetica-Bold", textAlign: "center", fontSize: 8 }]}>
              Describa las actividades realizadas para cada uno de los factores de acuerdo con el plan de trabajo de acompañamiento y asistencia técnica aprobado:
            </Text>
          </View>

          {[
            { label: "Acompañamiento y fortalecimiento técnico y financiero",                                                                                                                                          value: formData?.avanceFactor1 },
            { label: "Fortalecimiento organizativo y asociativo",                                                                                                                                                      value: formData?.avanceFactor2 },
            { label: "Acompañamiento y fortalecimiento a procesos de mercadeo y comercialización",                                                                                                                    value: formData?.avanceFactor3 },
            { label: "Acompañamiento y gestión para la articulación e incidencia territorial",                                                                                                                        value: formData?.avanceFactor4 },
            { label: "Implementación de los enfoques de género, étnico y diferenciales",                                                                                                                             value: formData?.avanceFactor5 },
            { label: "Generación de condiciones para el acceso a tierra",                                                                                                                                             value: formData?.avanceFactor6 },
            { label: "Promoción de la articulación interinstitucional para la seguridad humana en los territorios",                                                                                                   value: formData?.avanceFactor7 },
            { label: "Acompañamiento para la implementación de los planes/programas de manejo ambiental (PMA) y el fortalecimiento de relaciones sostenibles entre seres humanos y otras formas de vida",           value: formData?.avanceFactor8 },
          ].map((item, index, arr) => (
            <View
              key={index}
              style={index < arr.length - 1 ? styles.tableRow : styles.tableRowLast}
            >
              <Text style={[styles.tdLabel, { width: "38%", fontFamily: "Helvetica-Bold", minHeight: 36, fontSize: 11, color: COLOR_TEXT  }]}>{item.label}</Text>
              <Text style={[styles.tdValue, { width: "62%", minHeight: 36, textAlign: "justify", lineHeight: 1.4 }]}>{item.value || ""}</Text>
            </View>
          ))}
        </View>

        {/* ══════════════════════════════════════
            ENTREGABLES
        ══════════════════════════════════════ */}
     <View style={styles.table}>
  
  {/* Header principal */}
  <View style={[styles.tableRow, { backgroundColor: COLOR_HEADER_BG }]}>
    <Text style={[styles.tdHeader, { flex: 1, borderRightWidth: 0 }]}>
      ENTREGABLE PRÓXIMA VISITA DE LA(S) ACTIVIDAD(ES) DESARROLLADA(S)
    </Text>
  </View>

  {/* Subheaders */}
  <View style={styles.tableRow}>
    <Text
      style={[
        styles.tdSubHeader,
        { flex: 1, borderRightWidth: 1, borderRightColor: COLOR_TEXT },
      ]}
    >
      NOMBRE ENTREGABLE
    </Text>
    <Text style={[styles.tdSubHeader, { flex: 1 }]}>
      FECHA DE CUMPLIMIENTO
    </Text>
  </View>

  {/* Contenido */}
  {Array.isArray(formData?.entregables) &&
  formData.entregables.length > 0 ? (
    formData.entregables.map((item, i) => (
      <View
        key={i}
        style={
          i < formData.entregables.length - 1
            ? styles.tableRow
            : styles.tableRowLast
        }
      >
        <Text style={[styles.td33, { flex: 1 }]}>
          {item?.nombre_entregable || ""}
        </Text>
        <Text style={[styles.td33Last, { flex: 1 }]}>
          {item?.fecha_entregable || ""}
        </Text>
      </View>
    ))
  ) : (
    <View style={styles.tableRowLast}>
      <Text
        style={[
          styles.td33Last,
          {
            flex: 1,
            textAlign: "center",
          },
        ]}
      >
      </Text>
    </View>
  )}
</View>

        {/* ══════════════════════════════════════
            HALLAZGOS
        ══════════════════════════════════════ */}
        <View style={[styles.sectionGrayRow, { marginTop: 12 }]}>
          <Text style={styles.sectionGrayText}>HALLAZGOS</Text>
        </View>
        <View style={styles.freeTextBox}>
          <Text style={styles.freeText}>{formData?.hallazgos || ""}</Text>
        </View>

        {/* ══════════════════════════════════════
            RECOMENDACIONES
        ══════════════════════════════════════ */}
        <View style={[styles.sectionGrayRow, { marginTop: 12 }]}>
          <Text style={styles.sectionGrayText}>RECOMENDACIONES</Text>
        </View>
        <View style={styles.freeTextBox}>
          <Text style={styles.freeText}>{formData?.recomendaciones || ""}</Text>
        </View>

        {/* ══════════════════════════════════════
            ACUERDOS
        ══════════════════════════════════════ */}
       <View style={styles.table}>
  
  {/* Header principal */}
  <View style={[styles.tableRow, { backgroundColor: COLOR_HEADER_BG }]}>
    <Text style={[styles.tdHeader, { flex: 1, borderRightWidth: 0 }]}>
      ACUERDOS
    </Text>
  </View>

  {/* Subheaders */}
  <View style={styles.tableRow}>
    <Text
      style={[
        styles.tdSubHeader,
        { flex: 1, borderRightWidth: 1, borderRightColor: COLOR_TEXT },
      ]}
    >
      COMPROMISO
    </Text>
    <Text
      style={[
        styles.tdSubHeader,
        { flex: 1, borderRightWidth: 1, borderRightColor: COLOR_TEXT },
      ]}
    >
      RESPONSABLE
    </Text>
    <Text style={[styles.tdSubHeader, { flex: 1 }]}>
      FECHA LÍMITE
    </Text>
  </View>

  {/* Contenido */}
  {Array.isArray(formData?.acuerdos) &&
  formData.acuerdos.length > 0 ? (
    formData.acuerdos.map((item, i) => (
      <View
        key={i}
        style={
          i < formData.acuerdos.length - 1
            ? styles.tableRow
            : styles.tableRowLast
        }
      >
        <Text style={styles.td33}>
          {item?.compromiso || ""}
        </Text>
        <Text style={styles.td33}>
          {item?.responsable || ""}
        </Text>
        <Text style={styles.td33Last}>
          {item?.fechaLimite || ""}
        </Text>
      </View>
    ))
  ) : (
    <View style={styles.tableRowLast}>
      <Text
        style={[
          styles.td33Last,
          {
            flex: 1,
            textAlign: "center",
          },
        ]}
      >
      </Text>
    </View>
  )}
</View>

        {/* ══════════════════════════════════════
            FIRMA PROFESIONAL ASISTENTE TÉCNICO
        ══════════════════════════════════════ */}
        <View style={styles.signatureTable} wrap={false}>
          {/* Fila: espacio para firma */}
          <View style={[styles.signatureTableRow, { minHeight: 80 }]}>
            <Text style={[styles.signatureLabelCell, { justifyContent: "flex-start", paddingTop: 6 }]}>
              FIRMA PROFESIONAL ASISTENTE TÉCNICO
            </Text>
            <View style={styles.signatureImgCell}>
              {formData?.firmaProfesionalAsistenteTecnico ? (
                <Image style={styles.signatureImg} src={baseUrl + formData.firmaProfesionalAsistenteTecnico} />
              ) : null}
            </View>
          </View>
          {/* Nombre */}
          <View style={styles.signatureTableRow}>
            <Text style={styles.signatureLabelCell}>NOMBRE Y APELLIDO PROFESIONAL ASISTENCIA TÉCNICA</Text>
            <Text style={styles.signatureValueCell}>{formData?.nombreProfesionalAsistenteTecnico || ""}</Text>
          </View>
          {/* CC */}
          <View style={styles.signatureTableRow}>
            <Text style={styles.signatureLabelCell}>DOCUMENTO DE IDENTIDAD NÚMERO</Text>
            <Text style={styles.signatureValueCell}>{formData?.ccProfesionalAsistenteTecnico || ""}</Text>
          </View>
          {/* Cargo */}
          <View style={styles.signatureTableRowLast}>
            <Text style={styles.signatureLabelCell}>CARGO Y PROFESIÓN</Text>
            <Text style={styles.signatureValueCell}>{formData?.ProfesionProfesionalAsistenteTecnico || ""}</Text>
          </View>
        </View>

        {/* ══════════════════════════════════════
            ANEXOS — evidencia fotográfica
        ══════════════════════════════════════ */}
        <View style={styles.annexBlock}>
          <View style={styles.annexHeader}>
            <Text style={styles.annexHeaderText}>ANEXO 1</Text>
          </View>

          <PhotoBlock
            label="1. Fotografía con buena definición del profesional de asistencia técnica con el titular del proyecto productivo/delegado:"
            photos={formData.fotografia_participantes}
            baseUrl={baseUrl}
          />

          <PhotoBlock
            label="2. Fotografía de buena definición en el lugar donde se desarrolla el proyecto productivo (externa y interna)"
            photos={formData.fotografias_lugar}
            baseUrl={baseUrl}
          />

          <PhotoBlock
            label="3. Fotografía de buena definición que evidencie el funcionamiento del proyecto productivo"
            photos={formData.fotografia_finalidad}
            baseUrl={baseUrl}
          />

          <PhotoBlock
            label="4. Fotografía de buena definición donde se evidencie una actividad propia del proyecto productivo con el profesional de asistencia técnica"
            photos={formData.fotografia_actividad}
            baseUrl={baseUrl}
            isLast
          />
        </View>

        {/* ══════════════════════════════════════
            AVISO LEGAL — pegado al fondo de la última página
        ══════════════════════════════════════ */}
        <View style={styles.footer}>
           <Text style={[{ fontSize: 9, textAlign: "center", fontFamily: "Helvetica-Bold", marginBottom: 5}]}>Aviso de privacidad</Text>
          <Text style={styles.footerText}>
            El registro de datos personales, autoriza a la entidad para la recolección,
            almacenamiento y uso de los mismos conforme a las disposiciones contenidas
            en la Ley 1581 de 2012 y las normas que la modifiquen, adicionen o complementen.
            Conozca la Política de Privacidad y Tratamiento de Datos Personales a través de
            http://www.reincorporacion.gov.co/es/atencion/Paginas/Aviso-de-privacidad.aspx
          </Text>
          <Text style={styles.footerCode}>
            Código Único: {formData?.codigo_pdf || "N/A"}
          </Text>
        </View>

      </Page>
    </Document>
  );
};

// ─────────────────────────────────────────────
//  COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────
const PdfVisitaTecnicaSostenibilidad = ({
  formData: initialFormData,
  onClose,
}) => {
  const [data, setData] = useState(initialFormData || {});
  const [reactPdfPreview, setReactPdfPreview] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const mergedPdfRef = useRef(null);
  const reactPdfRef = useRef(null);
  const isDesktop = useMediaQuery({ minWidth: 768 });

  const generateReactPreview = async () => {
    setLoading(true);
    try {
      const blob = await pdf(<MyPDF formData={data} />).toBlob();
      reactPdfRef.current = blob;
      setReactPdfPreview(URL.createObjectURL(blob));
    } finally {
      setLoading(false);
    }
  };

  const mergePdfs = async () => {
    setLoading(true);
    try {
      const pdfUrl = `${baseUrl}${data.anexoFirmas}?t=${new Date().getTime()}`;
      const reactPdfBlob = await pdf(<MyPDF formData={data} />).toBlob();
      const pdf1 = await PDFDocument.load(await reactPdfBlob.arrayBuffer());
      const pdf2 = await PDFDocument.load(
        await fetch(pdfUrl).then((res) => res.arrayBuffer())
      );
      const mergedPdf = await PDFDocument.create();
      (await mergedPdf.copyPages(pdf1, pdf1.getPageIndices())).forEach((p) =>
        mergedPdf.addPage(p)
      );
      (await mergedPdf.copyPages(pdf2, pdf2.getPageIndices())).forEach((p) =>
        mergedPdf.addPage(p)
      );
      const mergedBytes = await mergedPdf.save();
      const blob = new Blob([mergedBytes], { type: "application/pdf" });
      mergedPdfRef.current = blob;
      setPreviewUrl(URL.createObjectURL(blob));
      message.success("PDF unificado generado ✅");
    } catch (error) {
      console.error(error);
      message.error("Error generando PDF unificado ❌");
    } finally {
      setLoading(false);
    }
  };

  const buildFileName = ({
    modalidadSecion = "Visita técnica",
    fecha_registro,
    numero_visita = "1",
    unificado = false,
  }) => {
    const equivalencias = {
      "Visita técnica": "Visita",
      "Gira técnica": "Gira",
      "Centros demostrativos de demostración": "Centro_Demostrativo",
      ECA: "Eca",
      Taller: "Taller",
      "Dia de campo": "Dia_Campo",
      Otro: "Otro",
    };
    let nombre =
      equivalencias[modalidadSecion] || modalidadSecion.replace(/\s+/g, "_");
    let fecha = fecha_registro || new Date().toISOString().split("T")[0];
    fecha = fecha.replace(/-/g, "_");
    return `1_${nombre}_${numero_visita}_${fecha}.pdf`;
  };

  const handleDownload = () => {
    const blobRef =
      data.modoFirma === "anexo_firma_participantes"
        ? mergedPdfRef.current
        : reactPdfRef.current;
    if (!blobRef) return message.warning("No hay PDF disponible");
    const url = URL.createObjectURL(blobRef);
    const link = document.createElement("a");
    link.href = url;
    link.download = buildFileName({
      modalidadSecion: data?.modalidad_sesion_capacitacion,
      fecha_registro: data?.fecha_registro,
      numero_visita: data?.numero_visita,
      unificado: data.modoFirma === "anexo_firma_participantes",
    });
    link.click();
  };

  const activePreviewUrl =
    data.modoFirma === "anexo_firma_participantes"
      ? previewUrl
      : reactPdfPreview;

  useEffect(() => {
    if (!data) return;
    if (data.modoFirma === "sin_anexo_firma_participantes" || !data.modoFirma) {
      generateReactPreview();
    } else if (data.modoFirma === "anexo_firma_participantes") {
      mergePdfs();
    }
  }, [data]);

  if (!data) return <div>Cargando...</div>;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        backgroundColor: "#e8edf4",
        overflow: "hidden",
      }}
    >
      {/* ══ BARRA SUPERIOR FIJA ══ */}
      <div
        style={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "0 20px",
          height: 56,
          backgroundColor: "#052956",
          boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
          zIndex: 10,
        }}
      >
        {/* Botón volver */}
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={onClose}
          style={{
            flexShrink: 0,
            backgroundColor: "rgba(255,255,255,0.12)",
            borderColor: "rgba(255,255,255,0.25)",
            color: "#fff",
            fontWeight: 600,
          }}
        >
          {isDesktop ? "Volver" : null}
        </Button>

        {/* Título centrado */}
        <div style={{ flex: 1, overflow: "hidden", textAlign: "center" }}>
          <div
            style={{
              color: "#fff",
              fontWeight: 700,
              fontSize: isDesktop ? 15 : 13,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {isDesktop
              ? "Reporte de Visita Técnica de Sostenibilidad"
              : "Visita Técnica"}
          </div>
          {(data?.fecha_registro || data?.numero_visita) && (
            <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 11, marginTop: 1 }}>
              {[
                data?.numero_visita && `N° ${data.numero_visita}`,
                data?.fecha_registro,
              ]
                .filter(Boolean)
                .join(" · ")}
            </div>
          )}
        </div>

        {/* Botón descargar */}
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          loading={loading}
          onClick={handleDownload}
          style={{
            flexShrink: 0,
            backgroundColor: "#1a6abf",
            borderColor: "#1a6abf",
            fontWeight: 600,
          }}
        >
          {isDesktop ? "Descargar PDF" : null}
        </Button>
      </div>

      {/* ══ CONTENIDO PRINCIPAL ══ */}
      {loading ? (
        /* Pantalla de carga */
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 16,
          }}
        >
          <style>{`
            @keyframes spin { to { transform: rotate(360deg); } }
            .pdf-spinner {
              width: 44px; height: 44px;
              border: 4px solid #c9d8ed;
              border-top-color: #052956;
              border-radius: 50%;
              animation: spin 0.85s linear infinite;
            }
          `}</style>
          <div className="pdf-spinner" />
          <span style={{ fontSize: 14, fontWeight: 600, color: "#052956" }}>
            Generando documento…
          </span>
        </div>
      ) : isDesktop ? (
        /* ══ DESKTOP: visor PDF full-height ══ */
        <div
          style={{
            flex: 1,
            overflow: "hidden",
             paddingTop: 16,
             paddingBottom: 16,
             paddingLeft: 150,
             paddingRight: 150,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Barra gris estilo visor */}
          <div
            style={{
              flexShrink: 0,
              height: 36,
              backgroundColor: "#404040",
              borderRadius: "8px 8px 0 0",
              display: "flex",
              alignItems: "center",
              paddingInline: 14,
              gap: 6,
            }}
          >
            {["#ff5f57","#ffbd2e","#28c840"].map((c) => (
              <div
                key={c}
                style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: c }}
              />
            ))}
            <span
              style={{
                marginLeft: 10,
                color: "rgba(255,255,255,0.5)",
                fontSize: 11,
                fontFamily: "monospace",
              }}
            >
              {buildFileName({
                modalidadSecion: data?.modalidad_sesion_capacitacion,
                fecha_registro: data?.fecha_registro,
                numero_visita: data?.numero_visita,
              })}
            </span>
          </div>

          {/* iframe del PDF */}
          <div
            style={{
              flex: 1,
              borderRadius: "0 0 8px 8px",
              overflow: "hidden",
              boxShadow: "0 6px 28px rgba(0,0,0,0.22)",
            }}
          >
            {activePreviewUrl ? (
              <iframe
                src={`${activePreviewUrl}#toolbar=1&navpanes=0`}
                style={{ width: "100%", height: "100%", border: "none", display: "block" }}
                title="Vista previa del PDF"
              />
            ) : (
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#525659",
                  color: "#ccc",
                  fontSize: 14,
                }}
              >
                No se pudo generar la vista previa.
              </div>
            )}
          </div>
        </div>
      ) : (
        /* ══ MÓVIL: tarjeta de descarga ══ */
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 24,
            padding: "32px 24px",
          }}
        >
          {/* Ícono documento */}
          <div
            style={{
              position: "relative",
              width: 72,
              height: 90,
              backgroundColor: "#fff",
              borderRadius: "6px 18px 6px 6px",
              boxShadow: "0 8px 28px rgba(5,41,86,0.18)",
              border: "1.5px solid #d0dcea",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: -1,
                right: -1,
                width: 20,
                height: 20,
                backgroundColor: "#e8edf4",
                borderRadius: "0 0 0 8px",
                borderLeft: "1.5px solid #d0dcea",
                borderBottom: "1.5px solid #d0dcea",
              }}
            />
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" fill="#052956" opacity="0.15"/>
              <path d="M14 2v6h6" stroke="#052956" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="#052956" strokeWidth="1.5"/>
              <path d="M9 13h6M9 17h4" stroke="#052956" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>

          {/* Mensaje */}
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#052956", marginBottom: 8 }}>
              PDF listo
            </div>
            <div style={{ fontSize: 13, color: "#6b7a90", lineHeight: 1.6, maxWidth: 280 }}>
              La vista previa no está disponible en dispositivos móviles.
              Descarga el archivo para abrirlo.
            </div>
          </div>

          {/* Botón principal */}
          <button
            onClick={handleDownload}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              backgroundColor: "#052956",
              color: "#fff",
              border: "none",
              borderRadius: 10,
              padding: "14px 32px",
              fontSize: 15,
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: "0 4px 16px rgba(5,41,86,0.3)",
              width: "100%",
              maxWidth: 300,
              justifyContent: "center",
            }}
          >
            <DownloadOutlined style={{ fontSize: 18 }} />
            Descargar PDF
          </button>

          {/* Metadatos */}
          {(data?.fecha_registro || data?.numero_visita || data?.municipio) && (
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: 10,
                padding: "14px 18px",
                width: "100%",
                maxWidth: 300,
                boxShadow: "0 2px 10px rgba(0,0,0,0.07)",
                border: "1px solid #e0e7f0",
              }}
            >
              {[
                { label: "Visita N°", value: data?.numero_visita },
                { label: "Fecha", value: data?.fecha_registro },
                { label: "Municipio", value: data?.municipio },
              ]
                .filter((r) => r.value)
                .map((row, i, arr) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      fontSize: 12,
                      color: "#444",
                      paddingBlock: 5,
                      borderBottom: i < arr.length - 1 ? "1px solid #f0f2f5" : "none",
                    }}
                  >
                    <span style={{ fontWeight: 600, color: "#052956" }}>{row.label}</span>
                    <span>{row.value}</span>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PdfVisitaTecnicaSostenibilidad;